// MapboxGLControl.js

import { useCallback, useRef, useEffect, useState } from "react"
import mapboxgl from "mapbox-gl"
import { SwmmGeoJSON } from '@fileops/swmm-node'
import { deleteLink, deleteNode, deleteSubcatchment, subdivideLink } from '../SwmmNode_Staging/EditModel'
import proj4 from "proj4"
import { updateCoords, createFullLine, createVertices } from '../SwmmNode_Staging/UpdateCoords'

mapboxgl.accessToken =
process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

/**
 * This control uses the official Mapbox library to display and control mapbox interactions. 
 * 
 * @param {*} param0 
 * @returns 
 */
const MapboxGLControl = ({ height = '400px', geoJSONData, view, model, setModel, loadModel, setOpen, isOpen, handleClose, setTargetType, setTargetID, editType, setEditType, mapStyle, showLabels }) => {
  let upstreamNode = ''
  const movingObjectIDRef = useRef('')
  const [movingPolygon, setMovingPolygon] = useState({})
  const [startPoint, setStartPoint] = useState([])
  const [isMovingObject, setIsMovingObject] = useState(0)
  const isMovingObjectRef = useRef()
  isMovingObjectRef.current = isMovingObject
  const [hoverInfo, setHoverInfo] = useState(null)
  const [drawGeoJSON, setDrawGeoJSON] = useState({
    type: "FeatureCollection",
    features: []
  })
  const [drawGeoJSONPoints, setDrawGeoJSONPoints] = useState({
    type: "FeatureCollection",
    features: []
  })
  const [drawGeoJSONPolygon, setDrawGeoJSONPolygon] = useState({
    type: "FeatureCollection",
    features: []
  })
  const [startLngLat, setStartLngLat] = useState([0,0])
  const startLngLatRef = useRef()
  startLngLatRef.current = startLngLat
  const [midLngLats,   setMidLngLats] = useState([])
  const midLngLatsRef = useRef()
  midLngLatsRef.current = midLngLats

  const geoJSONRef = useRef()
  geoJSONRef.current = geoJSONData
  const drawGeoJSONRef = useRef()
  drawGeoJSONRef.current = drawGeoJSON
  const drawGeoJSONPointsRef = useRef()
  drawGeoJSONPointsRef.current = drawGeoJSONPoints
  const mapRef = useRef()
  const mapContainerRef = useRef(null)
  const editStateRef = useRef()
  editStateRef.current = editType
  const modelRef = useRef()
  modelRef.current = model
  const createLineRef = useRef(createLine)
  createLineRef.current = createLine

  // Map styles
  const mapStyles = ['mapbox://styles/mapbox/dark-v11', 'mapbox://styles/mapbox/satellite-v9', 'mapbox://styles/mapbox/light-v11', 'mapbox://styles/mapbox/outdoors-v12' ]
  
  useEffect(()=>{
    if( mapRef.current ){
      mapRef.current.mapStyle=mapStyles[mapStyle]
    }
  }, [mapStyle])

  const onHover = useCallback(event => {
    const object = event.features && event.features[0]
    setHoverInfo({
      longitude: event.lngLat.lng,
      latitude: event.lngLat.lat,
      objectName: object && object.properties.name
    })
  }, [])

  useEffect(()=>{
    geoJSONRef.current = geoJSONData
    if(!!geoJSONData && !!geoJSONData.features && !!(geoJSONData.features.length > 0)){
      if(mapRef.current !== undefined){
        if(mapRef.current.getSource !== undefined){
          if(mapRef.current.getSource("modeldata") !== undefined){
            try{
              let source = mapRef.current.getSource("modeldata")
              if(source)
                source.setData(geoJSONRef.current)
            } catch(e){
              console.log(e)
              console.log('Will re-attempt on next load cycle.')
            }
          }
        }
      }
    }
  }, [geoJSONData])

  const mapContainerStyle = {
    height: '100%'
  }
  
  useEffect(()=>{
    // If editType has been changed to 0, remove any objects in edit layer.
    if(editStateRef.current === 0){
      setDrawGeoJSONPoints({
        type: "FeatureCollection",
        features: []
      })
    }
  }, [editType, isMovingObject])

  useEffect(()=>{
    // If isMovingObject has been changed to 0, remove any objects in edit layer.
    isMovingObjectRef.current = isMovingObject
    if(editStateRef.current === 0){
      setDrawGeoJSONPolygon({
        type: "FeatureCollection",
        features: []
      })
    }
  }, [isMovingObject])

  function createLine(e) {
    if([4, 10, 12, 14, 16, 17].includes(editStateRef.current)){
      setDrawGeoJSON({
        type: "FeatureCollection",
        features: [{
          geometry: {
            type: 'LineString', 
            coordinates:[
              [startLngLatRef.current[0], startLngLatRef.current[1]],
              ...midLngLatsRef.current,
              [e.lngLat.lng, e.lngLat.lat]
            ]
          },
          properties: {
            name: 'drawline',
            layer: 'LINKS-EDIT'
          },
          type: "Feature"
        }]
      })
    } else {
      setDrawGeoJSON({type: "FeatureCollection",
      features: []})
    }
  }

  function deProjection(e, model, geojson) {
    if(model && ('OPTIONS' in model)){
      let zone = Math.ceil((e.lngLat.lng+180)/6)
      let utmZone = zone.toString()
      // Define the source zone (x / y type)
      proj4.defs(`EPSG:326${utmZone}`, `+proj=utm +zone=${utmZone} +datum=WGS84 +units=m +no_defs`)

      let selectedProjection = 'swmmNode_proj'
      let units = model.PCS[selectedProjection].units

      let utmProjection = `+proj=utm +zone=${utmZone} +datum=WGS84 +units=${units} +no_defs`
      let transform = proj4(utmProjection)

      setModel(prev=>({...prev, 
        PCS: {
          ...prev.PCS, swmmNode_proj: {
            ...prev.PCS.swmmNode_proj, 
            zone: zone
          } 
        },
        
        TRANSFORM: {
          ...prev.TRANSFORM, swmmNode_t: {
            ...prev.TRANSFORM.swmmNode_t, 
            x: transform.forward([e.lngLat.lng, e.lngLat.lat])[0], 
            y: transform.forward([e.lngLat.lng, e.lngLat.lat])[1]
          }
        }
      }))

      return
    }
  }

  
  // End the move of a raingage
  function endGageMovement(e){
    e.preventDefault()

    let swmmCoord = updateCoords(e, modelRef.current)
    
    // Create the model structures for:
    // [SYMBOLS]
    let coordinatesJSON = { 
      [movingObjectIDRef.current]:{
        x:swmmCoord[0],
        y:swmmCoord[1]     
      }     
    }
    
    setModel(prev=>({
      ...prev, 
      SYMBOLS: {
        ...prev.SYMBOLS, ...coordinatesJSON
      }
    }))

    movingObjectIDRef.current = ''
    setIsMovingObject(0)
  }
  
  // End the move of a point
  function endPointMovement(e){
    e.preventDefault()

    let swmmCoord = updateCoords(e, modelRef.current)
    
    // Create the model structures for:
    // [COORDINATES]
    let coordinatesJSON = { 
      [movingObjectIDRef.current]:{
        x:swmmCoord[0],
        y:swmmCoord[1]     
      }     
    }
    
    setModel(prev=>({
      ...prev, 
      COORDINATES: {
        ...prev.COORDINATES, ...coordinatesJSON
      }
    }))

    movingObjectIDRef.current = ''
    setIsMovingObject(0)
  }

  // End the move of an object
  function endPolygonMovement(e){
    e.preventDefault()

    let newCentroid = updateCoords(e, modelRef.current)

    let coordsArray = modelRef.current.POLYGONS[movingObjectIDRef.current]
    let newCoordsArray = coordsArray.map(c=>({
      x: c.x + (newCentroid[0] - startPoint[0]),
      y: c.y + (newCentroid[1] - startPoint[1])
    }))

    let coordinatesJSON = {
      [movingObjectIDRef.current]: newCoordsArray
    }
    
    setModel(prev=>({
      ...prev, 
      POLYGONS: {
        ...prev.POLYGONS, ...coordinatesJSON
      }
    }))

    setDrawGeoJSONPolygon({type: "FeatureCollection", features: []})

    movingObjectIDRef.current = ''
    setIsMovingObject(0)
  }

  // Initialize map when component mounts
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapStyles[mapStyle]//"mapbox://styles/mapbox/streets-v11"
    });
    mapRef.current = map

    map.on("load", function () {
      map.addSource("drawGeoJSONPoints", {
        type: "geojson",
        data: drawGeoJSONPointsRef.current,
      })

      map.addSource("drawGeoJSON", {
        type: "geojson",
        data: drawGeoJSONRef.current,
      })

      map.addSource("modeldata", {
        type: "geojson",
        data: geoJSONRef.current,
      })

      // Add an image to use as a custom marker
      function loadImages () {
        map.loadImage(
          'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAACXBIWXMAAAsSAAALEgHS3X78AAADh0lEQVRogd1agZGcMAyUv4FcCXQQdxA6CCWQDq4ESiAVhO+AEq4E0gHXAV+BM3zkH72QZQOGu8QzmvmfM1grrWRZxjjnIPcwxpQAcAEAy149ogzOuSnnslmAGGMqAKhQ8a+Jj70BwA0A+ll2A5uBbBEAKACgBYBZAZdBOgAoN+uz+oG/lOkyKS/J7CW7Vq9V1DLGXAGgAYAvkal3HwvoMT987KTQ7+e8VjLlVnihj1hy/r2e5ya+s0qg5pDqnZQFLb5QWmhCDyUpr6xRowdDa1S7qGWMschZiUpB1+NzFhMCHROm3ltgPY26P5xz3WpqoSKS20fJ3UiVbkUWE6mINL4FnqlXUQtfJtFpEBbWaJEiIj0DmXEKxUwIiBTYHZtTKJbbIrMxSsFIEiMWMSmBuEqeYHPKjBshlytbq5VoqQJBSnEFP1kgYKXcwr0vMaTUgEi8tMwTR4Pw0jAD8zgcRSDIef6ylv1+FJ1CUkWMWEtAOBcnRqmcgZ0qMR0GCQi3NnXtGXERki7iFfsBBDczPoFaYs8+kUMKogvf31oKhAd5Tx6UQJ4trcKOkQLhFq+dnslOj5VIUir8jxqtzs5UIbEKvaoXbBTQcfcVLVaxsUPUWYPqObA17QtuNnSMdMKTgACmy8h+u7wIylK0/DzxyEF1ET3CR9Z+00FjoaME5J8c/xUQHjg0OzwTzXhbiY5RAkKzGA+qRw6qi5hpL3wDYmeUZ9gMHT1ICVVwHSpR6Dkg1pg7Rdghi6/5kX45hSgH+yeg1Sv5m8fHm3NuCFWUE7PAo+utUmFIR6tfyV20Am4eCOIWqXwrfkLk5fro9MP/WWKTdEw+3J/bQfHSRLzRLIAE0ho//EvNu6OkX6Ubm2w1jp54YhyYAaUY/dyRpP8E2kJO6PwdGfw9AyH1DIaF3gKQUCe+ZvOqA9JyIzCEryF25EPd+NDdSCuAzkG1xQWoYijxjkQEEmkD9cJdhr+qXpuixStphbpdSN/Y1du84/8SfrqjZRZXaNiwKLWrN0wg0rMFgvsmrPnqnKuDyoYQEuvUSizc9lzyM49qFA16IkotIWY02gwIuFih/AXpq1XXk3ZvmEwt5nYf2N8jU38jMO2DARugD39P/V7ZpowNNCgPrrsmvtkl6bWD17XyIcEWmY2z+eODXUFK4mdL6vXW71K+bMgWIykD06f/6kH98AxL8DzNDQD4A3vfj4AO1jt0AAAAAElFTkSuQmCC',
          function (error, image) {
            if (error) throw error;
            if(map.hasImage('junction'))
              map.removeImage("junction")
            map.addImage("junction", image);
          }
        )
        map.loadImage(
          'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAsCAYAAAAn4+taAAAACXBIWXMAAAsSAAALEgHS3X78AAACjklEQVRogd2a7VHDMAyGJY7/dAPKBO0GdAPYgIzQETJCOwFhAsoG6QbdgLBBmECcOTXnOvJHHLttqjsfJUptvbb8RMEgEcEtGALAHAC+J67lCXhFSgCgibbyXwMLmQFAM0EhKuZZJ4TFFBMUUnTxHz+wmNqm+JLNkjH1SeyGkJWgurwCIdIeXun39PCLiBUAvJlUIKKG/SsWnNPUbNc8nkTVDyIqTq4I6tUXW0P9zvDnzv25Nt7O8LW6X0yt0KXMjOtSGyc41V15aW6ug+abCauWorU6XNSYJnxs8d478nwNAJ/a7wtEVLiriKhFROV/N76zZ/KFmJrtZ3NM1TfvDbUHFkJMsnloYeLYnLEoXPtwalnx2tmnZ8ClkC6bsbgO2IMbwb+MFuLoVKdKFUIVDxUrDxU33jgD08C6zD5cC/05cepL52gh3Pk6AscroR9nKlr866AYQ24iGceNx38Q+nDi1DdGKiG+2XwV/IXml6rr16GrOlpISP7a/AH7bDBuxwqRiKITR8S1D6cC+chFvtFCHCmgByUFbcWpRfzgV4cYITFpMjgdswuhsI0r4bqHUx8gsgshC0qNmZb+mNFofqne6iH7HEJiHm6DH6LZhVBAnWXkv6+s6eh3CSFDCsDoQjO7kJAUYX/U6+u5hYS8JI1+GcsuhOw47mE09L6LCbHMtPngG11PnUuIs9TwlTZXI4QcxZ+v2EzRkp5YIeJx4z9ol7/454t27Zdx2yYbPOWsOFIoOW7NluUMERHVqjxa3D9ENE895l3qDtmKSF+0ZRHCRwJ7wbU/HhektmzH05Zzje6cJbXlSi3ggLfapW0uEZBzReAUx5Act4a5jhVGGx8/KBxDThHKbuNfOADgD+Ydm+EKjaHeAAAAAElFTkSuQmCC',
          function (error, image) {
            if (error) throw error;
            if(map.hasImage('outfall'))
              map.removeImage("outfall")
            map.addImage("outfall", image);
          }
        )
        map.loadImage('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADEAAAAvCAYAAABKQCL3AAAACXBIWXMAAAsSAAALEgHS3X78AAAAsUlEQVRoge3asQ3CMBQG4TOihxEyAhvAKGwCozBKRoFJHkoJcgpSoEv0f5KbVO8iWbaiNGAArnx6VtUDodbavTfVBaivNVYVxtWZtXbGt/2rRFgkwiIRFvuZOYa5Q8Wqd9itamVPWGx6Y78A5S0WuPUe5hZrkAiLRFgkwiIRFomwSIRFIiwSYZEIi0RYJMIiERZznzFPrbVx7REH4PznWRbLnrDYRMT0l81x2siCWZYB3gnxMSwINc6xAAAAAElFTkSuQmCC', 
          function (error, image) {
            if (error) throw error;
            if(map.hasImage('storage'))
              map.removeImage("storage")
            map.addImage("storage", image);
          }
        )
        map.loadImage('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAACXBIWXMAAAsSAAALEgHS3X78AAABaklEQVRogc3a0Y3CMAwGYJcFWIEROhIj3EgdgRFuBEZgBDYw8gkkBL02Tv7ftiW/FGr7Izw0USdVFWZM0zRbeVW9MvscAhC/li8QK2iQN8TxmVyM/bXQKSI28N3Kf6Rdmyk9AxGvpGCiETRMBoKCiUDcdz6DYCIQc8t3UiGeAdmYEEQEJgzBxoQimJhwBAuTgqD8MFkIdO1UBLJHOgLVqwQC0bMMYrR3KcTIDOUQvbOURPTMVBbhxVicdnZ2aQgH5nRQ1ZuIXDYOWn4QpzWDMT+PlNbi8md4Uy8bq7Ikrsa5Za7Pm0phWhFfkEoYD2IVUgHjRfwLycT0IDYhGZhexC4kEjOCaIJEYEYRzRAmBoFwQRgYFMINQWKQiC4IAoNGdENGMAzEEKQHw0IMQzwYJgICacGwETBIA4aKgEI6MNBnNSjEgcE/cKILNmAoWwAKZAPD28ewCq9gqDvLiPe1DGPNztQ+bEhIiMgDj7MnWV+bOhAAAAAASUVORK5CYII=', 
          function (error, image) {
            if (error) throw error;
            if(map.hasImage('divider'))
              map.removeImage("divider")
            map.addImage("divider", image);
          }
        )
        map.loadImage('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeAAAAAXNSR0IArs4c6QAABWZJREFUeF7tWk+I3FQY/71J6+5CCy20YKEHwQr2UNjFLpuMLcTNLLigoKCo2IMHexJBUbDQeqtIQdDD3urBg+KCgoIKhZ2MgZZ9WbvFPRQsVPDggkILLriwu3UyT5LsZJJM/rxMXjJTNnPL5Hvf+77f9/u+9733QrDHf2SP+48KgIoBexyBKgX2OAGqIlilwFBTQFX3YUf6z7GB6kMJxlAm9UBXNBYIwBBAGB4AYeddJNqg+v4yWTkcAPzO21Gva1fB8Oau4wao/kxZIJQPQNj5rqeydg8ER9xH6ySocacMEMoFQNH+BXDAcWzMOgjD2Aw4GQdOgUiUB4CsNUCw5PpCFkCbb0f6VTII5QGQxbEssjnZUQ4AWR2aaZxBjV13fOuQs1hp3sjpZ+zw4gGQNQoCOXOzkxW0AREqHoCeIz+C6s9nsrMEEIoFIK8DqnoAO5K9cgBb1mGsGRuZAOQQLg4ART0BSHczUz9sdF4QU0AoEABfn5+3x/dAYO+Ctj7jCCy3SDEAyA0VhP2cO/pdN5TGXYCdEKbPB08xAPRouwmqH+QOR5JgT+c8qH5NiE67JROlKLKlzUt9v1J/Gy1Qr3gAiixaXd0PBQAPrKO4ZdwXyrAeuNdA9XkRuotjgMAoeY7K2tcgeFVkMRQLQH32Ihi5LNLAvigLTgOxABSZ/96SuNtfCGJYUQAUd6z1kDBAWJGqUiBubzASKVDX3gPDJ96lhv90V5CBqQyYVA/l2SVmqwGK9h2AF/qM8jsrOEcDc/lPjrtzKtqfAI775FZB9WneHiEdgP4J+nVHAQBkPwBJszpqVxh9wdLVtA2qTySpjQcgXvEaqD4Vq7SoNFA0e3epJvYYcQy1B8UcqPQDEOW43dyYzQ8jnVa0twAsBAwroh/o6mRYhKm/5swnz74Bs/VFpF2y9joIvkxM18BusK5Ogkm/BgbEFTJZuw6CM7HKn1KP4BHpnvue3QFtnUxjd+L7OED9/zPch6kfjQlS8BKWsUswWx/Zsi4D6tpLYPjGGzxm7YdhtCMq8B8AHouchGEOpt703inaTQCnd58Hu/T0nwnaisIByXrBGpR3apQLAA9lw5MRfI5l/Xxy5GZ/A8iTngyxprBsrHGxITxf0rKqzL4DkE+52Bs6VyBczvsjwdhlmK3oehDl2dPqaXQkmw3BX4coWGmagT8Vzb46a6TlbSyAM6qMmkTdzEusW15K9ACwatP4ZWk1Unm34YmiIVc4HZZtARjnFXfkBjkK7zFnA1Q/HDvfrlwPgLZ1DDeNv9MGOO+josfrmf/aK2oMww2Y+lledZ6c3RFOSP94z2mdaB8AadENFyRvJusJUOP3zAaLGKCq49iRbGYFfwQvY1n/lieY4SKYXq3TqbwKUjuP5SW+YscLxIx6HDXpKoBnE4ekR95e6icdHW3rWHcZ9H+iwvfFVn1uEqwT7Bt4nREtx3AOpv5VqtrgyuIc2fc6QaVxAWAfB5SkoekXdq7Caj8Elr1UiwYS2ECHzPetIEmqFM2uDYd6Iuw2aOuU/czXCpPalBBKuzdGjzvX5R2Mg3i7uE3YnVwNt9Eht2A2jYGg8Q+Kq1mhJi96MzStPop90l+RRhB2AcutK7kNLEKBrL0CgsUY1S+C6t/318s0Q5K3m/boNTyw5oTfAaTZ5X5lap9PPJenKKafBwTy3PeVV5qBznu2DtQWwfBTZlrb/QLpaCA4BxD3YpT3N2ZNwDC2ecSzARDWGNzw8MxXhMw6tqxTgx6L5QMgyh27+GzXPgAh72dufRPhIQvotK9gxVgXiaJ4AERaV4KuCoASQB7pKSoGjHR4SjCuYkAJII/0FBUDRjo8JRj3P3cBDdjgnkvnAAAAAElFTkSuQmCC', 
          function (error, image) {
            if (error) throw error;
            if(map.hasImage('raingage'))
              map.removeImage("raingage")
            map.addImage("raingage", image);
          }
        )
      }

      loadImages()

      map.on('styleimagemissing', ()=>{
        loadImages()
      })

      // Add a polygon layer
      map.addLayer({
        id: "polygons",
        type: "fill",
        source: "modeldata",
        paint: {
          'fill-color': 'rgba(80, 120, 180, 0.7)',
        },
        'filter': ['==', '$type', 'Polygon']
      });
      // Add a polygon outline layer
      map.addLayer({
        id: "outlines",
        type: "line",
        source: "modeldata",
        paint: {
          'line-color': 'black',
          'line-opacity':0.9,
          'line-width': 1
        },
        'filter': ['==', '$type', 'Polygon']
      });

      // Add a line layer
      map.addLayer({
        id: "lines",
        type: "line",
        source: "modeldata",
        paint: {
          'line-color': 'black',
          'line-opacity':0.9,
          'line-width': 3
          /*'line-width': [
            "interpolate",
            ["linear"],
            ["zoom"],
            0, 0.1,
            14, 1,
            22, 5
          ]*/
        },
        'filter': ['==', '$type', 'LineString']
      });

      
      // Add a drawing line layer
      map.addLayer({
        id: "LINKS-EDIT",
        type: "line",
        source: "drawGeoJSON",
        paint: {
          'line-color': 'yellow',
          'line-opacity':0.9,
          'line-width': 5
        },
        'filter': ['==', '$type', 'LineString']
      });

      // Add a symbol layer
      map.addLayer({
        id: "points",
        type: "symbol",
        source: "modeldata",
        layout: {
          'icon-image': [
            'match', ['get','layer'],
            'JUNCTIONS', 'junction',
            'STORAGE', 'storage',
            'DIVIDERS', 'divider',
            'OUTFALLS', 'outfall',
            'RAINGAGES', 'raingage',
            'junction'
          ],
          // get the title name from the source's "title" property
          "text-field": [
            'match', ['get','layer'],
            'JUNCTIONS', ["get", "name"],
            'STORAGE', ["get", "name"],
            'DIVIDERS', ["get", "name"],
            'OUTFALLS', ["get", "name"],
            'RAINGAGES', ["get", "name"],
            ''
          ],
          "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
          "text-offset": [0.5, 0.5],
          "text-anchor": "top",
          "text-size": (showLabels?16:0),
          //'icon-size':0.3,
          'icon-size': [
            "interpolate",
            ["linear"],
            ["zoom"],
            0, 0.1,
            14, 0.2,
            22, 0.3
          ],
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "text-allow-overlap": true,
          "text-ignore-placement": true,
        },
        paint: {
          //'icon-color': 'rgba(250, 122, 0, 0.8)',
          "text-halo-color": "#fff",
          //"text-halo-width": 2,
          'text-halo-width':[
            "interpolate",
            ["linear"],
            ["zoom"],
            0, 0.1,
            14, 1,
            22, 2
          ],
          'text-color': '#000'
        },
        'filter': ['==', '$type', 'Point']
      })

      
      // Add an editable point layer
      map.addLayer({
        id: "POINTS-EDIT",
        type: "circle",
        source: "drawGeoJSONPoints",
        paint: {
          'circle-radius': 10,
          'circle-color': '#007cbf',
        },
        //'circle-opacity':0.9,
        'filter': ['==', '$type', 'Point']
      })

      
      // Change the cursor to a pointer when hovering over the marker
      map.on('mouseenter', ['points', 'lines', 'polygons'], () => {
        map.getCanvas().style.cursor = 'default';
      });

      // Change it back to the default cursor when it leaves
      map.on('mouseleave', ['points', 'lines', 'polygons'], () => {
        map.getCanvas().style.cursor = '';
      });

      
      map.on('mouseup', (e)=>{ if((isMovingObject === 3) && editStateRef.current === 100){ endPolygonMovement(e) } else if((isMovingObjectRef.current === 1) && editStateRef.current === 100){ endPointMovement(e) } else if((isMovingObject === 6) && editStateRef.current === 100){ endGageMovement(e) }})

      // Center the map on the coordinates of any clicked circle from the 'circle' layer.
      map.on('click', ['points', 'lines', 'polygons', 'outlines', 'POINTS-EDIT'], (e) => {
        if(e.features.length > 0 && editStateRef.current === 0){ 
          setOpen(false)
          showModal( 
            !isOpen, 
            e.features[0].properties.layer, e.features[0].properties.name
          )
        } else if(e.features.length > 0 && e.features[0].properties.layer !== 'LINKS-EDIT'){ 
          mapClickEffect(e) 
        }
      })
      map.on('click', function(e){mapClickEffect(e)})
      map.on('mousedown', (e)=> {if(e && e.features && e.features.length > 0 && editStateRef.current === 100){  mapClickEffect(e) } })
      map.on('mousemove', (e)=>{onHover(e); if((isMovingObject === 3) && editStateRef.current === 100){ mapClickEffect(e) }; createLineRef.current(e); } )
      
      map.on('mousedown', 'points', (e) => {
        // Prevent the default map drag behavior.
        //e.preventDefault();
        
        /*
        map.on('mousemove', onMove);
        map.once('mouseup', onUp);*/
        //mapClickEffect(e)
      });
    })

    // Add navigation control (the +/- zoom buttons)
    //map.addControl(new mapboxgl.NavigationControl(), "top-right");

    // Clean up on unmount
    return () => map.remove();
  }, [ loadModel, mapStyle, showLabels ]);

  useEffect(()=>{
    if( mapRef.current && model?.PCS != null){
      if(view == null){
        let boundbox = SwmmGeoJSON.getBounds(model)
        let bounds = [[boundbox.topright.lng,   boundbox.topright.lat],
                      [boundbox.bottomleft.lng, boundbox.bottomleft.lat]]
        mapRef.current.fitBounds(
          bounds,
          {padding: 40, duration: 0}
        )
        mapRef.current.setPitch(0)
        mapRef.current.setBearing(0)
      } else {
        mapRef.current.fitBounds(view.bounds, {padding: 40, duration: 0})
        mapRef.current.setPitch(view.pitch)
        mapRef.current.setBearing(view.bearing)
      }
    }
  }, [loadModel, view, mapStyle, showLabels])

  
  useEffect(()=>{
    drawGeoJSONRef.current = drawGeoJSON
    if(mapRef.current !== undefined){
      if(mapRef.current.getSource !== undefined){
        if(mapRef.current.getSource("drawGeoJSON") !== undefined){
          try{
            let source = mapRef.current.getSource("drawGeoJSON")
            if(source)
              source.setData(drawGeoJSONRef.current)
          } catch(e){
            console.log(e)
            console.log('Will re-attempt on next load cycle.')
          }
        }
      }
    }
  }, [drawGeoJSON])

  useEffect(()=>{
    drawGeoJSONPointsRef.current = drawGeoJSONPoints
    if(mapRef.current !== undefined){
      if(mapRef.current.getSource !== undefined){
        if(mapRef.current.getSource("drawGeoJSONPoints") !== undefined){
          try{
            let source = mapRef.current.getSource("drawGeoJSONPoints")
            if(source)
              source.setData(drawGeoJSONPointsRef.current)
          } catch(e){
            console.log(e)
            console.log('Will re-attempt on next load cycle.')
          }
        }
      }
    }
  }, [drawGeoJSONPoints])

  
  function showModal(isopen, layername, t1){
    async function stateChange() {
    }
    //setOpen(isopen)
    handleClose()
    setTargetType(layername.toUpperCase())
    setTargetID(t1)
    stateChange().then(()=>{
      setOpen(true)
    })
  }

  function mapClickEffect(e) {
  
    if(editStateRef.current === 1){
      let features = mapRef.current.queryRenderedFeatures(
        {layers: ['building'], filter: ['==', 'extrude', 'true']}
      )

      let thegeoJ = 
      { "type":"FeatureCollection",
        "features": 
        features.filter(feature=>feature.geometry.type !== 'MultiPolygon').map(feature=>{
          return {
            "type":"Feature",
            "geometry": {
              "type": "Polygon",
              "coordinates": feature.geometry.coordinates
            },
            "properties": {
              "name": feature.id.toString(),
              "layer": "SUBCATCHMENTS"
            }
          }
        })
      }

      deProjection(e, modelRef.current, thegeoJ)
      setEditType(0)
    } 




    // Select a node to move
    else if(editStateRef.current === 100 && isMovingObjectRef.current === 0 && e.features && e.features[0].properties && ['OUTFALLS','JUNCTIONS','DIVIDERS', 'STORAGE'].includes(e.features[0].properties.layer)){
      e.preventDefault()
      setIsMovingObject(1)
      movingObjectIDRef.current = e.features[0].properties.name
    }

    // Select a raingage to move
    else if(editStateRef.current === 100 && isMovingObjectRef.current === 0 && e.features && e.features[0].properties && ['RAINGAGES'].includes(e.features[0].properties.layer)){
      setIsMovingObject(6)
      e.preventDefault()
      movingObjectIDRef.current = e.features[0].properties.name
    }



    // Create a proxy polygon and move that around instead of constantly updating the .inp file.
    else if(editStateRef.current === 100 && isMovingObject === 3 ){
      e.preventDefault()

      let newCoordsArray = movingPolygon.geometry.coordinates[0].map(c=>[
        c[0] + (e.lngLat.lng - startLngLat[0]),
        c[1] + (e.lngLat.lat - startLngLat[1])
      ])
      
      setDrawGeoJSONPolygon({
        type: "FeatureCollection",
        features: [{
          geometry: {
            type: 'Polygon', 
            coordinates:[
              newCoordsArray
            ]
          },
          properties: {
            name: 'drawpolygon',
            layer: 'POLYGON-EDIT'
          },
          type: "Feature"
        }]
      })
    }

    // Select a polygon to move
    else if(editStateRef.current === 100 && isMovingObjectRef.current === 0 && e.features && e.features[0].properties && ['SUBCATCHMENTS'].includes(e.features[0].properties.layer)){
      setIsMovingObject(3)
      e.preventDefault()
      movingObjectIDRef.current = e.features[0].properties.name
      setMovingPolygon(e.features[0])
      setStartLngLat([e.lngLat.lng, e.lngLat.lat])
      setStartPoint(updateCoords(e, modelRef.current))
    }
    
    // Create a new JUNCTIONS
    else if(editStateRef.current === 2){
      // Translate the coordinates into something usable by the model
      let swmmCoord = updateCoords(e, modelRef.current)

      // Get the next name for nodes:
      let newNodeName = ''
      let usedNodeNames = [
        ...Object.keys(modelRef.current.SUBCATCHMENTS),  
        ...Object.keys(modelRef.current.COORDINATES),
      ]
      for(let i=1; i<100000 && newNodeName === ''; i++){
        if(!usedNodeNames.includes(i.toString())){
          newNodeName = i.toString()
        }
      }

      // Create the model structures for:
      // [COORDINATES]
      let coordinatesJSON = { 
        [newNodeName]:{
          x:swmmCoord[0],
          y:swmmCoord[1]     
        }     
      }

      // [JUNCTIONS]
      let junctionsJSON = {
        [newNodeName]: {
          Invert: 0,
          Dmax: 7,
          Dinit: 0,
          Dsurch: 0,
          Aponded: 5,
          Description: ""
        }
      }

      setModel(prev=>({
        ...prev, 
        COORDINATES: {
          ...prev.COORDINATES, ...coordinatesJSON
        },
        JUNCTIONS: {
          ...prev.JUNCTIONS, ...junctionsJSON
        }
      }))
    } // Create a new SUBCATCHMENTS
    else if(editStateRef.current === 5){
      setDrawGeoJSONPoints({
        type: "FeatureCollection",
        features: [{
          geometry: {
            type: 'Point', 
            coordinates:[e.lngLat.lng, e.lngLat.lat]
          },
          properties: {
            name: 'drawpoint',
            layer: 'POINTS-EDIT'
          },
          type: "Feature"
        }]
      })
      setEditType(17)
      
      setStartLngLat([e.lngLat.lng, e.lngLat.lat])
      setMidLngLats([])
      
      // Create an artificial line from the start node to the mouse pointer
    } 
    // Create a new OUTFALLS
    else if(editStateRef.current === 6){
      // Translate the coordinates into something usable by the model
      let swmmCoord = updateCoords(e, modelRef.current)

      // Get the next name for nodes:
      let newNodeName = ''
      let usedNodeNames = [
        ...Object.keys(modelRef.current.SUBCATCHMENTS),  
        ...Object.keys(modelRef.current.COORDINATES),
      ]
      for(let i=1; i<100000 && newNodeName === ''; i++){
        if(!usedNodeNames.includes(i.toString())){
          newNodeName = i.toString()
        }
      }

      // Create the model structures for:
      // [COORDINATES]
      let coordinatesJSON = { 
        [newNodeName]:{
          x:swmmCoord[0],
          y:swmmCoord[1]     
        }     
      }

      // [OUTFALLS]
      let outfallsJSON = {
        [newNodeName]: {
          "Invert": 0,
          "Type": "FREE",
          "Gated": "NO",
          "RouteTo": "", 
          Description: ''
        }
      }

      setModel(prev=>({
        ...prev, 
        COORDINATES: {
          ...prev.COORDINATES, ...coordinatesJSON
        },
        OUTFALLS: {
          ...prev.OUTFALLS, ...outfallsJSON
        }
      }))
    } 
    // Create a new DIVIDERS
    else if(editStateRef.current === 7){
      // Translate the coordinates into something usable by the model
      let swmmCoord = updateCoords(e, modelRef.current)

      // Get the next name for nodes:
      let newNodeName = ''
      let usedNodeNames = [
        ...Object.keys(modelRef.current.SUBCATCHMENTS),  
        ...Object.keys(modelRef.current.COORDINATES),
      ]
      for(let i=1; i<100000 && newNodeName === ''; i++){
        if(!usedNodeNames.includes(i.toString())){
          newNodeName = i.toString()
        }
      }

      // Create the model structures for:
      // [COORDINATES]
      let coordinatesJSON = { 
        [newNodeName]:{
          x:swmmCoord[0],
          y:swmmCoord[1]     
        }     
      }

      // [DIVIDERS]
      let dividersJSON = {
        [newNodeName]: {
          Invert: 0, 
          DivLink: '*', 
          Type: 'CUTOFF', 
          Qmax: 0,
          Dmax: 0, 
          Dinit: 0, 
          Dsurch: 0, 
          Aponded: 0, 
          Description: ''
        }
      }

      setModel(prev=>({
        ...prev, 
        COORDINATES: {
          ...prev.COORDINATES, ...coordinatesJSON
        },
        DIVIDERS: {
          ...prev.DIVIDERS, ...dividersJSON
        }
      }))
    }
    // Create a new STORAGE
    else if(editStateRef.current === 8){
      // Translate the coordinates into something usable by the model
      let swmmCoord = updateCoords(e, modelRef.current)

      // Get the next name for nodes:
      let newNodeName = ''
      let usedNodeNames = [
        ...Object.keys(modelRef.current.SUBCATCHMENTS),  
        ...Object.keys(modelRef.current.COORDINATES),
      ]
      for(let i=1; i<100000 && newNodeName === ''; i++){
        if(!usedNodeNames.includes(i.toString())){
          newNodeName = i.toString()
        }
      }

      // Create the model structures for:
      // [COORDINATES]
      let coordinatesJSON = { 
        [newNodeName]:{
          x:swmmCoord[0],
          y:swmmCoord[1]     
        }     
      }

      // [STORAGE]
      let storageJSON = {
        [newNodeName]: {
          "Invert": 0,
          "Dmax": 0,
          "Dinit": 0,
          "Curve": "FUNCTIONAL",
          "P1": 0,
          "P2": 0,
          "P3": 10000,
          "CurveName": "",
          "Dsurch": 0,
          "Fevap": 0,
          "Psi": "",
          "Ksat": "",
          "IMD": "",
          "Description": ""
        }
      }

      setModel(prev=>({
        ...prev, 
        COORDINATES: {
          ...prev.COORDINATES, ...coordinatesJSON
        },
        STORAGE: {
          ...prev.STORAGE, ...storageJSON
        }
      }))
    }
    // Create a new RAINGAGE
    else if(editStateRef.current === 18){
      // Translate the coordinates into something usable by the model
      let swmmCoord = updateCoords(e, modelRef.current)

      // Get the next name for raingages:
      let newNodeName = ''
      let usedNodeNames = Object.keys(modelRef.current.SYMBOLS)
      for(let i=1; i<100000 && newNodeName === ''; i++){
        if(!usedNodeNames.includes(i.toString())){
          newNodeName = i.toString()
        }
      }

      // Create the model structures for:
      // [SYMBOLS]
      let coordinatesJSON = { 
        [newNodeName]:{
          x:swmmCoord[0],
          y:swmmCoord[1]     
        }     
      }

      // [RAINGAGES]
      let RAINGAGES = {
        [newNodeName]: {
          "Format": "INTENSITY",
          "Interval": "1:00",
          "SCF": "1.0",
          "Source": "TIMESERIES",
          "SeriesName": "",
          "Description": ""
      }
      }

      setModel(prev=>({
        ...prev, 
        SYMBOLS: {
          ...prev.SYMBOLS, ...coordinatesJSON
        },
        RAINGAGES: {
          ...prev.RAINGAGES, ...RAINGAGES
        }
      }))
    }
    // If this click is the start of creating a new line element,
    // record the upstream node and the spatial location of the node.
    else if([3, 9, 11, 13, 15].includes(editStateRef.current) && e.features && e.features[0].properties && ['OUTFALLS','JUNCTIONS','DIVIDERS', 'STORAGE'].includes(e.features[0].properties.layer)){
      upstreamNode = e.features[0].properties.name
      setDrawGeoJSONPoints({
        type: "FeatureCollection",
        features: [{
          geometry: {
            type: 'Point', 
            coordinates:e.features[0].geometry.coordinates
          },
          properties: {
            name: 'drawpoint',
            layer: 'POINTS-EDIT'
          },
          type: "Feature"
        }]
      })
      switch(editStateRef.current){
        case 3:
          setEditType(4)
          break;
        case 9:
          setEditType(10)
          break;
        case 11:
          setEditType(12)
          break;
        case 13:
          setEditType(14)
          break;
        case 15:
          setEditType(16)
          break;
        default: 
          break
      }
      
      setStartLngLat(e.features[0].geometry.coordinates)
      setMidLngLats([])
      
      // Create an artificial line from the start node to the mouse pointer
    } else if([4, 10, 12, 14, 16].includes(editStateRef.current) && e.features && e.features[0].properties && ['OUTFALLS','JUNCTIONS','DIVIDERS', 'STORAGE'].includes(e.features[0].properties.layer))
    {
      // If the user clicked on the same node, assume it was a mistake and let them try again.
      if (e.features[0].properties.name === upstreamNode){
        return
      }
      
      setDrawGeoJSONPoints({
        type: "FeatureCollection",
        features: []
      })

      let linestringCoords = createFullLine(e, modelRef.current, upstreamNode, midLngLatsRef.current)
      let newentry = createVertices(e, modelRef.current, midLngLatsRef.current)
      
      // Get the next name for links:
      let newLinkName = ''
      let usedLinkNames = [
        ...Object.keys(modelRef.current.CONDUITS), 
        ...Object.keys(modelRef.current.PUMPS), 
        ...Object.keys(modelRef.current.ORIFICES), 
        ...Object.keys(modelRef.current.WEIRS), 
        ...Object.keys(modelRef.current.OUTLETS), 
      ]

      for(let i=1; i<100000 && newLinkName === ''; i++){
        if(!usedLinkNames.includes(i.toString())){
          newLinkName = i.toString()
        }
      }

      const polylineLength = (points) =>{
        let length = 0
        for (let i = 0; i < points.length - 1; i++) {
          const dx = points[i + 1][0] - points[i][0]
          const dy = points[i + 1][1] - points[i][1]
          length += Math.sqrt(dx * dx + dy * dy)
        }
        return length;
      }

      // For a new conduit:
      if(editStateRef.current === 4){
        // Create the model structures for:
        // [CONDUITS]
        let conduitsJSON = { 
          [newLinkName]:{
            "Node1": upstreamNode,
            "Node2": e.features[0].properties.name,
            "Length": polylineLength(linestringCoords).toFixed(2),
            "Roughness": 0.01,
            "InOffset": "0",
            "OutOffset": "0",
            "InitFlow": "0",
            "MaxFlow": "0",
            "Description": ""
          }     
        }

        // [XSECTIONS]
        let xsectionsJSON = {
          [newLinkName]: {
            "swmmType": "SHAPE",
            "Shape": "CIRCULAR",
            "Geom1": "1",
            "Geom2": "0",
            "Geom3": "0",
            "Geom4": "0",
            "Barrels": 1,
            "Culvert": ""
          }
        }

        // [LOSSES]
        let lossesJSON = {
          [newLinkName]: {
            "Kin": 0,
            "Kout": 0,
            "Kavg": 0,
            "Flap": "NO",
            "Seepage": 0
          }
        }

        // [VERTICES]
        let verticesJSON = {
          [newLinkName]: newentry
        }

        setModel(prev=>({
          ...prev, 
          CONDUITS: {
            ...prev.CONDUITS, ...conduitsJSON
          },
          XSECTIONS: {
            ...prev.XSECTIONS, ...xsectionsJSON
          },
          LOSSES: {
            ...prev.LOSSES, ...lossesJSON
          },
          VERTICES: {
            ...prev.VERTICES, ...verticesJSON
          }
        }))
      }

      // For a new pump:
      if(editStateRef.current === 10){
        // Create the model structures for:
        // [PUMPS]
        let pumpsJSON = { 
          [newLinkName]:{
            "Node1": upstreamNode,
            "Node2": e.features[0].properties.name,
            "Pcurve": "*",
            "Status": "ON",
            "Startup": 0,
            "Shutoff": 0,
            "Description": ""
          }
        }

        // [VERTICES]
        let verticesJSON = {
          [newLinkName]: newentry
        }

        setModel(prev=>({
          ...prev, 
          PUMPS: {
            ...prev.PUMPS, ...pumpsJSON
          },
          VERTICES: {
            ...prev.VERTICES, ...verticesJSON
          }
        }))
      }

      // For a new orifice:
      if(editStateRef.current === 12){
        // Create the model structures for:
        // [ORIFICES]
        let orificesJSON = { 
          [newLinkName]:{
            "Node1": upstreamNode,
            "Node2": e.features[0].properties.name,
            "Type": "SIDE",
            "Offset": "0",
            "Cd": 0.65,
            "Gated": "NO",
            "Orate": 0,
            "Description": ""
          }
        }

        // [XSECTIONS]
        let xsectionsJSON = {
          [newLinkName]: {
            "swmmType": "SHAPE",
            "Shape": "CIRCULAR",
            "Geom1": "1",
            "Geom2": "0",
            "Geom3": "0",
            "Geom4": "0",
            "Barrels": "",
            "Culvert": ""
          }
        }

        // [VERTICES]
        let verticesJSON = {
          [newLinkName]: newentry
        }

        setModel(prev=>({
          ...prev, 
          ORIFICES: {
            ...prev.ORIFICES, ...orificesJSON
          },
          XSECTIONS: {
            ...prev.XSECTIONS, ...xsectionsJSON
          },
          VERTICES: {
            ...prev.VERTICES, ...verticesJSON
          }
        }))
      }

      // For a new weir:
      if(editStateRef.current === 14){
        // Create the model structures for:
        // [WEIRS]
        let weirsJSON = { 
          [newLinkName]:{
            "Node1": upstreamNode,
            "Node2": e.features[0].properties.name,
            "Type": "TRANSVERSE",
            "CrestHt": "0",
            "Cd": 3.33,
            "Gated": "NO",
            "EC": 0,
            "Cd2": 0,
            "Surcharge": "YES",
            "Width": "",
            "Surface": "",
            "Wcurve": "",
            "Description": ""
          }
        }

        // [XSECTIONS]
        let xsectionsJSON = {
          [newLinkName]: {
            "swmmType": "SHAPE",
            "Shape": "RECT_OPEN",
            "Geom1": "1",
            "Geom2": "1",
            "Geom3": "0",
            "Geom4": "0",
            "Barrels": 1,
            "Culvert": ""
          }
        }

        // [VERTICES]
        let verticesJSON = {
          [newLinkName]: newentry
        }

        setModel(prev=>({
          ...prev, 
          WEIRS: {
            ...prev.WEIRS, ...weirsJSON
          },
          XSECTIONS: {
            ...prev.XSECTIONS, ...xsectionsJSON
          },
          VERTICES: {
            ...prev.VERTICES, ...verticesJSON
          }
        }))
      }

      // For a new outlet:
      if(editStateRef.current === 16){
        // Create the model structures for:
        // [OUTLETS]
        let outletsJSON = { 
          [newLinkName]:{
            "Node1": upstreamNode,
            "Node2": e.features[0].properties.name,
            "Offset": "0",
            "Type": "FUNCTIONAL/DEPTH",
            "C1": 10,
            "C2": 0.5,
            "Gated": "NO",
            "Description": ""
          }
        }

        // [VERTICES]
        let verticesJSON = {
          [newLinkName]: newentry
        }

        setModel(prev=>({
          ...prev, 
          OUTLETS: {
            ...prev.OUTLETS, ...outletsJSON
          },
          VERTICES: {
            ...prev.VERTICES, ...verticesJSON
          }
        }))
      }

      switch(editStateRef.current){
        case 4:
          setEditType(3)
          break;
        case 10:
          setEditType(9)
          break;
        case 12:
          setEditType(11)
          break;
        case 14:
          setEditType(13)
          break;
        case 16:
          setEditType(15)
          break;
        default:
          break
      }
    // Create an artificial line from the start node to the mouse pointer
  } else if([17].includes(editStateRef.current) && e.features && e.features[0].properties && ['POINTS-EDIT'].includes(e.features[0].properties.layer))
  {
    setDrawGeoJSONPoints({
      type: "FeatureCollection",
      features: []
    })

      // Get the next name for links:
      let newPolygonName = ''
      let usedPolygonNames = [
        ...Object.keys(modelRef.current.SUBCATCHMENTS),  
        ...Object.keys(modelRef.current.COORDINATES),
      ]

      for(let i=1; i<100000 && newPolygonName === ''; i++){
        if(!usedPolygonNames.includes(i.toString())){
          newPolygonName = i.toString()
        }
      }
    
    let newPolygonPoints = [...midLngLatsRef.current, [e.lngLat.lng, e.lngLat.lat]]
    
    let newentry = createVertices(e, modelRef.current, newPolygonPoints)
    
    function shoelaceFormula(points) {
      points = points.map(coord=>[coord.x, coord.y])
      let sum = 0;
      for (let i = 0; i < points.length - 1; i++) {
        sum += (points[i][0] * points[i+1][1]) - (points[i+1][0] * points[i][1]);
      }
      sum += (points[points.length-1][0] * points[0][1]) - (points[0][0] * points[points.length-1][1])
      // Use hectares for metric, acres for imperial
      if(modelRef.current.PCS['swmmNode_proj'].units==='m'){
        sum = sum * 0.0001
        sum = parseFloat(Math.abs(sum/2).toFixed(5))
      } else {
        sum = sum * 2.29568e-5
        sum = parseFloat(Math.abs(sum/2).toFixed(2))
      }

      return sum
    }

    // Create the model structures for the new subcatchment
    let SUBCATCHMENTS = { 
      [newPolygonName]:{
        "RainGage": "",
        "Outlet": "Unknown",
        "Area": shoelaceFormula(newentry),
        "PctImperv": (50+(40*parseFloat(Math.random()))).toFixed(2),
        "Width": Math.sqrt(shoelaceFormula(newentry)).toFixed(2),
        "PctSlope": (0.5+(0.5*parseFloat(Math.random()))).toFixed(2),
        "CurbLen": 0,
        "SnowPack": "",
        "Description": ""
      }
    }

    let SUBAREAS = { 
      [newPolygonName]:{
        "NImperv": 0.01,
        "NPerv": 0.1,
        "SImperv": 0.05,
        "SPerv": 0.05,
        "PctZero": 25,
        "RouteTo": "OUTLET",
        "PctRouted": null
      }
    }

    let INFILTRATION = { 
      [newPolygonName]:{
        "Param1": "0",
        "Param2": "0",
        "Param3": "0",
        "Param4": "0",
        "Param5": "0"
      }
    }

    // [POLYGONS]
    let POLYGONS = {
      [newPolygonName]: newentry
    }

    setModel(prev=>({
      ...prev, 
      SUBCATCHMENTS: {
        ...prev.SUBCATCHMENTS, ...SUBCATCHMENTS
      },
      SUBAREAS: {
        ...prev.SUBAREAS, ...SUBAREAS
      },
      INFILTRATION: {
        ...prev.INFILTRATION, ...INFILTRATION
      },
      POLYGONS: {
        ...prev.POLYGONS, ...POLYGONS
      }
    }))

    //mapRef.current.off('mousemove', createLineRef.current)
    setEditType(5)
  }
    // If this click is the middle of creating a new line element,
    // record the upstream node and the spatial location of the node.
    else if([4, 10, 12, 14, 16, 17].includes(editStateRef.current) ){
      setMidLngLats(prev=>[...prev, [ e.lngLat.lng, e.lngLat.lat]])
    }
    // Delete a subcatchment
    else if(editStateRef.current === 40 && e.features){
      if( e.features[0].properties && e.features[0].properties.layer){
        deleteSubcatchment(modelRef.current, setModel, e.features[0].properties.layer, e.features[0].properties.name)
      }
    }
    // Delete a conduit
    else if(editStateRef.current === 30 && e.features){
      if( e.features[0].properties && e.features[0].properties.layer){
        deleteLink(modelRef.current, setModel, e.features[0].properties.layer, e.features[0].properties.name)
      }
    }
    // Delete a node (also delete connected links)
    else if(editStateRef.current === 20 && e.features){
      if( e.features[0].properties && e.features[0].properties.layer){
        deleteNode(modelRef.current, setModel, e.features[0].properties.layer, e.features[0].properties.name)
      }
    }
    // Subdivide a conduit
    else if(editStateRef.current === 1001 && e.features){
      if( e.features[0].properties && e.features[0].properties.layer){
        let divisionCount = 5;
        subdivideLink(modelRef.current, setModel, e.features[0].properties.layer, e.features[0].properties.name, divisionCount)
      }
    }
  }

  return (
  <div className="map-container" ref={mapContainerRef} style={mapContainerStyle} >
  </div>
  )
};

export default MapboxGLControl;